import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  Link2, Unlink, Search, Loader2, AlertTriangle, RefreshCw, CheckCircle2, XCircle,
} from "lucide-react";

const LINK_TYPES = [
  { key: "inquiry", label: "Inquiry", api: "inquiry" },
  { key: "quotation", label: "Quotation", api: "quotation" },
  { key: "purchase_order", label: "Purchase Order", api: "purchase-order" },
  { key: "order_acceptance", label: "Order Acceptance", api: "order-acceptance" },
];

export default function CommercialLinkage({ cylinderId, onLinkageChange }) {
  const { user } = useAuth();
  const [linkage, setLinkage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [picker, setPicker] = useState(null); // { linkType, existing }
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [linking, setLinking] = useState(false);
  const [replaceConfirm, setReplaceConfirm] = useState(null); // { linkType, target, existing }
  const [unlinkConfirm, setUnlinkConfirm] = useState(null); // { linkType, label }

  const isAdmin = user && ["admin", "super_admin"].includes(user.role);

  async function loadLinkage() {
    setLoading(true);
    try {
      const { data } = await api.get(`/cylinders/${cylinderId}/linkage`);
      setLinkage(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Failed to load linkage");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadLinkage(); /* eslint-disable-next-line */ }, [cylinderId]);

  function openPicker(linkType) {
    const existing = linkage?.[linkType];
    setPicker({ linkType, existing });
    setQ("");
    setResults([]);
    doSearch("", linkType);
  }

  async function doSearch(text, linkType) {
    setSearching(true);
    try {
      const conf = LINK_TYPES.find((t) => t.key === linkType);
      const { data } = await api.get(`/cylinders/link/search/${conf.api}?q=${encodeURIComponent(text)}&limit=20`);
      setResults(data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Search failed");
    } finally {
      setSearching(false);
    }
  }

  async function performLink(target, opts = {}) {
    setLinking(true);
    const conf = LINK_TYPES.find((t) => t.key === picker.linkType);
    try {
      const url = `/cylinders/${cylinderId}/link/${conf.api}` + (opts.replace ? "?replace=true" : "");
      const { data } = await api.post(url, { id: target.id });
      let msg = `Linked ${conf.label} ${target.number}`;
      if (data.cascade_updates && Object.keys(data.cascade_updates).length) {
        msg += ` (cascade: ${Object.keys(data.cascade_updates).join(", ")})`;
      }
      toast.success(msg);
      if (data.duplicate_warning) {
        toast.warning(data.duplicate_warning);
      }
      setPicker(null);
      setReplaceConfirm(null);
      await loadLinkage();
      onLinkageChange && onLinkageChange();
    } catch (e) {
      // 409 → propose Replace
      if (e.response?.status === 409 && e.response.data?.detail?.existing) {
        const detail = e.response.data.detail;
        setReplaceConfirm({
          linkType: picker.linkType,
          target,
          existing: detail.existing,
        });
      } else {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Link failed");
      }
    } finally {
      setLinking(false);
    }
  }

  async function performUnlink() {
    const conf = LINK_TYPES.find((t) => t.key === unlinkConfirm.linkType);
    try {
      await api.delete(`/cylinders/${cylinderId}/link/${conf.api}`);
      toast.success(`Unlinked ${conf.label}`);
      setUnlinkConfirm(null);
      await loadLinkage();
      onLinkageChange && onLinkageChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Unlink failed");
    }
  }

  if (loading) {
    return (
      <Card className="border-slate-200 p-5">
        <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
      </Card>
    );
  }

  return (
    <>
      <Card className="border-slate-200" data-testid="commercial-linkage-card">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: "Chivo, sans-serif" }}>
              <Link2 className="w-4 h-4 text-red-700" />
              Commercial Link Manager
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              Attach or replace historical commercial documents to this cylinder.
            </div>
          </div>
          <Button size="sm" variant="ghost" onClick={loadLinkage} data-testid="linkage-refresh">
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
        </div>

        <div className="divide-y divide-slate-100">
          {LINK_TYPES.map((t) => {
            const item = linkage?.[t.key];
            return (
              <div key={t.key} className="px-5 py-3 flex items-center justify-between gap-3" data-testid={`linkage-row-${t.key}`}>
                <div className="min-w-[140px]">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{t.label}</div>
                  <div className="mt-1 text-sm">
                    {item ? (
                      <span className="font-mono bg-emerald-50 border border-emerald-200 text-emerald-900 rounded px-2 py-0.5" data-testid={`linkage-value-${t.key}`}>
                        {item.number}
                      </span>
                    ) : (
                      <span className="text-slate-400 italic" data-testid={`linkage-empty-${t.key}`}>Not linked</span>
                    )}
                  </div>
                  {item && (
                    <div className="mt-1 text-xs text-slate-500">
                      {[item.customer_name, item.date, item.status].filter(Boolean).join(" · ")}
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openPicker(t.key)}
                    data-testid={`btn-link-${t.key}`}
                  >
                    <Link2 className="w-3.5 h-3.5 mr-1" />
                    {item ? "Replace" : "Link"}
                  </Button>
                  {item && isAdmin && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-red-200 text-red-700 hover:bg-red-50"
                      onClick={() => setUnlinkConfirm({ linkType: t.key, label: t.label })}
                      data-testid={`btn-unlink-${t.key}`}
                    >
                      <Unlink className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Picker dialog */}
      <Dialog open={!!picker} onOpenChange={(o) => !o && setPicker(null)}>
        <DialogContent className="max-w-2xl" data-testid="linkage-picker-dialog">
          <DialogHeader>
            <DialogTitle>
              Link {LINK_TYPES.find((t) => t.key === picker?.linkType)?.label} to Cylinder
            </DialogTitle>
            <DialogDescription>
              Search existing records by number. Cascading links (parent docs) will be filled automatically.
            </DialogDescription>
          </DialogHeader>

          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
            <Input
              autoFocus
              className="pl-9"
              placeholder="Type number to search…"
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                doSearch(e.target.value, picker.linkType);
              }}
              data-testid="linkage-search-input"
            />
          </div>

          <div className="max-h-[400px] overflow-y-auto border rounded" data-testid="linkage-results">
            {searching ? (
              <div className="p-6 text-center text-slate-400">
                <Loader2 className="w-5 h-5 animate-spin mx-auto" />
              </div>
            ) : results.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-500">No matching records.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wider text-slate-500 bg-slate-50 sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2">Number</th>
                    <th className="text-left px-3 py-2">Customer</th>
                    <th className="text-left px-3 py-2">Date</th>
                    <th className="text-left px-3 py-2">Status</th>
                    <th className="text-right px-3 py-2">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {results.map((r) => (
                    <tr key={r.id} className="hover:bg-slate-50" data-testid={`linkage-result-${r.id}`}>
                      <td className="px-3 py-2 font-mono font-semibold text-slate-900">{r.number}</td>
                      <td className="px-3 py-2 text-slate-700">{r.customer_name || "—"}</td>
                      <td className="px-3 py-2 text-slate-500">{r.date || "—"}</td>
                      <td className="px-3 py-2">
                        <span className="text-xs bg-slate-100 rounded px-1.5 py-0.5">{r.status || "—"}</span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Button
                          size="sm"
                          onClick={() => performLink(r)}
                          disabled={linking}
                          className="bg-red-700 hover:bg-red-800 text-white"
                          data-testid={`btn-select-${r.id}`}
                        >
                          {linking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Select"}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {picker?.existing && (
            <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2">
              <AlertTriangle className="w-3 h-3 inline mr-1" />
              Currently linked: <strong className="font-mono">{picker.existing.number}</strong>. Selecting another record will replace it.
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setPicker(null)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Replace confirmation */}
      <Dialog open={!!replaceConfirm} onOpenChange={(o) => !o && setReplaceConfirm(null)}>
        <DialogContent data-testid="replace-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="w-5 h-5" />
              Replace existing link?
            </DialogTitle>
            <DialogDescription>
              This cylinder is currently linked to a different {LINK_TYPES.find((t) => t.key === replaceConfirm?.linkType)?.label}.
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm space-y-2">
            <div>Existing: <span className="font-mono bg-slate-100 rounded px-2 py-0.5">{replaceConfirm?.existing.number}</span></div>
            <div>Replace with: <span className="font-mono bg-emerald-50 border border-emerald-200 rounded px-2 py-0.5">{replaceConfirm?.target.number}</span></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReplaceConfirm(null)}>Cancel</Button>
            <Button
              className="bg-red-700 hover:bg-red-800 text-white"
              onClick={() => performLink(replaceConfirm.target, { replace: true })}
              disabled={linking}
              data-testid="btn-confirm-replace"
            >
              {linking ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Yes, Replace
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unlink confirmation */}
      <Dialog open={!!unlinkConfirm} onOpenChange={(o) => !o && setUnlinkConfirm(null)}>
        <DialogContent data-testid="unlink-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Unlink className="w-5 h-5" />
              Unlink {unlinkConfirm?.label}?
            </DialogTitle>
            <DialogDescription>
              This will remove the {unlinkConfirm?.label} reference from this cylinder. The audit trail is preserved.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUnlinkConfirm(null)}>Cancel</Button>
            <Button
              className="bg-red-700 hover:bg-red-800 text-white"
              onClick={performUnlink}
              data-testid="btn-confirm-unlink"
            >
              <XCircle className="w-4 h-4 mr-2" />
              Unlink
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
